// Vite's worker URL pipeline transpiles the TypeScript worklet and bundles its
// pure imports into a browser-loadable JavaScript module. Plain `?url` would
// emit the original `.ts` source, which AudioWorklet cannot execute in prod.
import workletModuleUrl from "./audio-worklet-processor.ts?worker&url";
import {
  PCM_FRAME_DURATION_MS,
  PCM_FRAME_SAMPLES,
  TARGET_SAMPLE_RATE,
} from "./audio-config";
import type {
  WorkletCommand,
  WorkletMessage,
} from "./audio-worklet-processor";

/** Must match the name registered by audio-worklet-processor.ts. */
export const PCM_CAPTURE_PROCESSOR_NAME = "long-form-pcm-capture";
export const PCM_CAPTURE_SAMPLE_RATE = TARGET_SAMPLE_RATE;
export const PCM_CAPTURE_FRAME_SAMPLES = PCM_FRAME_SAMPLES;
export const PCM_CAPTURE_FRAME_DURATION_MS = PCM_FRAME_DURATION_MS;

export type AudioCaptureState =
  | "idle"
  | "starting"
  | "capturing"
  | "paused"
  | "stopped"
  | "unavailable";

/**
 * A fixed 20 ms, mono Float32 PCM frame produced by the worklet. The samples
 * array is owned by the page after the worklet transfers its ArrayBuffer.
 */
export interface AudioFrame {
  samples: Float32Array;
  validSamples: number;
  rms: number;
  peak: number;
  durationMs: number;
}

export interface AudioCaptureSnapshot {
  state: AudioCaptureState;
  targetSampleRate: number;
  actualSampleRate?: number;
  inputChannelCount?: number;
  error?: string;
}

export type AudioFrameHandler = (frame: AudioFrame) => void;
export type AudioCaptureStateHandler = (snapshot: AudioCaptureSnapshot) => void;

export interface AudioCaptureOptions {
  /** Test hook; production uses the Vite-emitted URL for the worklet module. */
  workletModuleUrl?: string;
  /** Test hook; production creates an interactive AudioContext. */
  createAudioContext?: (options: AudioContextOptions) => AudioContext;
  /** A frame callback can also be supplied later with setFrameHandler(). */
  onFrame?: AudioFrameHandler;
  onStateChange?: AudioCaptureStateHandler;
  commandTimeoutMs?: number;
  now?: () => number;
}

interface PendingCommand {
  expected: "flushed" | "resumed" | "reset";
  resolve: () => void;
  reject: (error: Error) => void;
  timeout: ReturnType<typeof setTimeout>;
}

const DEFAULT_COMMAND_TIMEOUT_MS = 1_000;

const initialSnapshot: AudioCaptureSnapshot = {
  state: "idle",
  targetSampleRate: PCM_CAPTURE_SAMPLE_RATE,
};

/**
 * Browser-facing PCM capture graph. It deliberately owns only AudioContext
 * resources, never the MediaStream or its tracks, so RecorderEngine can keep
 * the same microphone stream alive for MediaRecorder and local uploads.
 */
export class AudioCapture {
  private readonly moduleUrl: string;
  private readonly contextFactory: (
    options: AudioContextOptions,
  ) => AudioContext;
  private readonly commandTimeoutMs: number;
  private readonly now: () => number;
  private snapshot: AudioCaptureSnapshot = initialSnapshot;
  private frameHandler?: AudioFrameHandler;
  private readonly frameListeners = new Set<AudioFrameHandler>();
  private readonly stateListeners = new Set<AudioCaptureStateHandler>();
  private context?: AudioContext;
  private source?: MediaStreamAudioSourceNode;
  private worklet?: AudioWorkletNode;
  private silentOutput?: GainNode;
  private activeStream?: MediaStream;
  private generation = 0;
  private commandSequence = 0;
  private readonly pendingCommands = new Map<string, PendingCommand>();

  constructor(options: AudioCaptureOptions = {}) {
    this.moduleUrl = options.workletModuleUrl ?? workletModuleUrl;
    this.contextFactory =
      options.createAudioContext ??
      ((contextOptions) => new AudioContext(contextOptions));
    this.frameHandler = options.onFrame;
    this.commandTimeoutMs =
      options.commandTimeoutMs ?? DEFAULT_COMMAND_TIMEOUT_MS;
    this.now = options.now ?? (() => performance.now());
    if (options.onStateChange) this.stateListeners.add(options.onStateChange);
  }

  getSnapshot(): AudioCaptureSnapshot {
    return this.snapshot;
  }

  setFrameHandler(handler?: AudioFrameHandler): void {
    this.frameHandler = handler;
  }

  subscribeFrames(listener: AudioFrameHandler): () => void {
    this.frameListeners.add(listener);
    return () => this.frameListeners.delete(listener);
  }

  subscribe(listener: AudioCaptureStateHandler): () => void {
    this.stateListeners.add(listener);
    listener(this.snapshot);
    return () => this.stateListeners.delete(listener);
  }

  /**
   * Start a zero-gain worklet graph over an existing microphone stream. A
   * failed or unsupported worklet becomes a visible unavailable state rather
   * than throwing and risking the durable MediaRecorder workflow.
   */
  async start(stream: MediaStream): Promise<AudioCaptureSnapshot> {
    if (
      (this.snapshot.state === "capturing" || this.snapshot.state === "paused") &&
      this.activeStream === stream
    ) {
      return this.snapshot;
    }

    await this.teardownGraph();
    const generation = ++this.generation;
    this.update({
      state: "starting",
      targetSampleRate: PCM_CAPTURE_SAMPLE_RATE,
    });

    try {
      const context = this.createInteractiveContext();
      this.context = context;
      // start() is called by the user action that starts capture. Resuming here
      // avoids relying on a later timer, which browsers may reject as autoplay.
      await context.resume();
      if (generation !== this.generation) {
        await this.teardownGraph();
        return this.snapshot;
      }

      // This MVP intentionally streams only native 16 kHz PCM. Do not fall
      // back to main-thread/worklet resampling: a mismatched context is closed
      // before any worklet, source node, or streamed frame can be created.
      if (context.sampleRate !== PCM_CAPTURE_SAMPLE_RATE) {
        const actualSampleRate = context.sampleRate;
        await this.teardownGraph();
        this.markUnavailable(
          `PCM streaming requires a native ${PCM_CAPTURE_SAMPLE_RATE} Hz audio context; this browser provided ${actualSampleRate} Hz.`,
          actualSampleRate,
        );
        return this.snapshot;
      }

      await context.audioWorklet.addModule(this.moduleUrl);
      if (generation !== this.generation) {
        await this.teardownGraph();
        return this.snapshot;
      }

      const worklet = new AudioWorkletNode(context, PCM_CAPTURE_PROCESSOR_NAME, {
        numberOfInputs: 1,
        numberOfOutputs: 1,
        // Preserve the device's actual input channel layout so the processor
        // can do the required mono downmix itself. Output remains one silent
        // channel through the zero-gain node below.
        channelCountMode: "max",
        channelInterpretation: "speakers",
        outputChannelCount: [1],
        processorOptions: { sourceSampleRate: context.sampleRate },
      });
      const source = context.createMediaStreamSource(stream);
      const silentOutput = context.createGain();
      silentOutput.gain.setValueAtTime(0, context.currentTime);

      worklet.port.onmessage = (event: MessageEvent<WorkletMessage>) => {
        if (generation !== this.generation) return;
        this.handleWorkletMessage(event.data);
      };
      worklet.addEventListener("processorerror", () => {
        if (generation !== this.generation) return;
        this.markUnavailable("The audio capture processor stopped unexpectedly.");
      });

      source.connect(worklet);
      worklet.connect(silentOutput);
      silentOutput.connect(context.destination);

      this.source = source;
      this.worklet = worklet;
      this.silentOutput = silentOutput;
      this.activeStream = stream;

      this.update({
        state: "capturing",
        targetSampleRate: PCM_CAPTURE_SAMPLE_RATE,
        actualSampleRate: context.sampleRate,
        error: undefined,
      });
    } catch (error) {
      if (generation === this.generation) {
        await this.teardownGraph();
        this.markUnavailable(
          error instanceof Error
            ? error.message
            : "PCM streaming is unavailable in this browser.",
        );
      }
    }

    return this.snapshot;
  }

  /** Flush the partial native-16 kHz PCM frame without changing capture state. */
  async flush(): Promise<void> {
    if (!this.worklet) return;
    try {
      await this.sendCommand("flush", "flushed");
    } catch (error) {
      this.markUnavailable(
        error instanceof Error ? error.message : "PCM capture flush failed.",
      );
    }
  }

  /** Stop accepting future input after the worklet has emitted its final frame. */
  async pause(): Promise<void> {
    if (this.snapshot.state !== "capturing" || !this.worklet) return;
    try {
      await this.sendCommand("pause", "flushed");
      if (this.getSnapshot().state !== "unavailable") {
        this.update({ ...this.snapshot, state: "paused", error: undefined });
      }
    } catch (error) {
      this.markUnavailable(
        error instanceof Error ? error.message : "PCM capture pause failed.",
      );
    }
  }

  async resume(): Promise<void> {
    if (this.snapshot.state !== "paused" || !this.worklet) return;
    try {
      await this.sendCommand("resume", "resumed");
      if (this.getSnapshot().state !== "unavailable") {
        this.update({ ...this.snapshot, state: "capturing", error: undefined });
      }
    } catch (error) {
      this.markUnavailable(
        error instanceof Error ? error.message : "PCM capture resume failed.",
      );
    }
  }

  /** Flush and release only this graph; it never stops the shared microphone. */
  async stop(): Promise<void> {
    if (this.worklet && this.snapshot.state === "capturing") {
      await this.flush();
    }
    await this.teardownGraph();
    if (this.snapshot.state !== "unavailable") {
      this.update({
        state: "stopped",
        targetSampleRate: PCM_CAPTURE_SAMPLE_RATE,
      });
    }
  }

  async dispose(): Promise<void> {
    await this.stop();
    this.frameListeners.clear();
    this.stateListeners.clear();
    this.frameHandler = undefined;
  }

  private createInteractiveContext(): AudioContext {
    return this.contextFactory({
      latencyHint: "interactive",
      sampleRate: PCM_CAPTURE_SAMPLE_RATE,
    });
  }

  private handleWorkletMessage(message: WorkletMessage | undefined): void {
    if (!message || typeof message.type !== "string") return;

    switch (message.type) {
      case "frame": {
        if (
          !(message.samples instanceof ArrayBuffer) ||
          message.samples.byteLength !== PCM_CAPTURE_FRAME_SAMPLES * Float32Array.BYTES_PER_ELEMENT
        ) {
          this.markUnavailable("The PCM capture processor emitted an invalid frame.");
          return;
        }
        const frame: AudioFrame = {
          samples: new Float32Array(message.samples),
          validSamples: Math.min(
            PCM_CAPTURE_FRAME_SAMPLES,
            Math.max(0, Math.floor(message.validSamples)),
          ),
          rms: Number.isFinite(message.rms) ? message.rms : 0,
          peak: Number.isFinite(message.peak) ? message.peak : 0,
          durationMs: PCM_CAPTURE_FRAME_DURATION_MS,
        };
        this.emitFrame(frame);
        break;
      }
      case "ready":
        this.log("worklet_ready", {
          actualSampleRate: message.inputSampleRate,
          targetSampleRate: message.targetSampleRate,
        });
        break;
      case "input_format":
        this.update({
          ...this.snapshot,
          inputChannelCount: message.channelCount,
        });
        this.log("input_format", { inputChannelCount: message.channelCount });
        break;
      case "flushed":
      case "resumed":
      case "reset":
        if (message.requestId) this.resolveCommand(message.requestId, message.type);
        break;
    }
  }

  private emitFrame(frame: AudioFrame): void {
    try {
      this.frameHandler?.(frame);
      for (const listener of this.frameListeners) listener(frame);
    } catch (error) {
      // Consumer failures must not tear down the shared recorder graph.
      this.log("frame_handler_error", {
        error: error instanceof Error ? error.message : "Unknown frame handler error",
      });
    }
  }

  private async sendCommand(
    type: WorkletCommand["type"],
    expected: PendingCommand["expected"],
  ): Promise<void> {
    const worklet = this.worklet;
    if (!worklet) return;

    const requestId = `pcm-${++this.commandSequence}-${Math.round(this.now())}`;
    const command: WorkletCommand = { type, requestId };
    await new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(() => {
        this.pendingCommands.delete(requestId);
        reject(new Error(`PCM capture ${type} timed out.`));
      }, this.commandTimeoutMs);
      this.pendingCommands.set(requestId, { expected, resolve, reject, timeout });

      try {
        worklet.port.postMessage(command);
      } catch (error) {
        clearTimeout(timeout);
        this.pendingCommands.delete(requestId);
        reject(
          error instanceof Error
            ? error
            : new Error(`Unable to send PCM capture ${type}.`),
        );
      }
    });
  }

  private resolveCommand(
    requestId: string,
    received: PendingCommand["expected"],
  ): void {
    const pending = this.pendingCommands.get(requestId);
    if (!pending || pending.expected !== received) return;
    clearTimeout(pending.timeout);
    this.pendingCommands.delete(requestId);
    pending.resolve();
  }

  private async teardownGraph(): Promise<void> {
    this.generation += 1;
    for (const [requestId, pending] of this.pendingCommands) {
      clearTimeout(pending.timeout);
      pending.reject(new Error("PCM capture was released before the command completed."));
      this.pendingCommands.delete(requestId);
    }

    const context = this.context;
    const source = this.source;
    const worklet = this.worklet;
    const silentOutput = this.silentOutput;
    this.context = undefined;
    this.source = undefined;
    this.worklet = undefined;
    this.silentOutput = undefined;
    this.activeStream = undefined;

    try {
      source?.disconnect();
      worklet?.disconnect();
      silentOutput?.disconnect();
      worklet?.port.close();
    } catch {
      // Disconnecting a graph that has already been released is harmless.
    }

    if (context && context.state !== "closed") {
      try {
        await context.close();
      } catch {
        // A browser may already be tearing down this context.
      }
    }
  }

  private markUnavailable(error: string, actualSampleRate?: number): void {
    this.log("capture_unavailable", { error });
    this.update({
      ...this.snapshot,
      state: "unavailable",
      targetSampleRate: PCM_CAPTURE_SAMPLE_RATE,
      ...(actualSampleRate === undefined ? {} : { actualSampleRate }),
      error,
    });
    // A worklet error must never keep a hidden real-time graph alive. This
    // only releases nodes and this AudioContext; it does not stop stream tracks.
    if (this.context || this.source || this.worklet || this.silentOutput) {
      void this.teardownGraph();
    }
  }

  private update(snapshot: AudioCaptureSnapshot): void {
    this.snapshot = snapshot;
    for (const listener of this.stateListeners) listener(snapshot);
  }

  private log(event: string, metadata: Record<string, unknown>): void {
    if (import.meta.env.DEV) {
      console.debug("[pcm-capture]", { event, ...metadata });
    }
  }
}
